import { useLocation, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo } from 'react';

import {
  type SessionSummary,
  useForecastSessionsQuery,
} from 'src/services/api/getForecastSessions';

const EMPTY_SESSIONS: SessionSummary[] = [];
const SESSION_SCOPED_PATHS = ['/forecast', '/configuration'];

type UseForecastSessionResult = {
  sessionId: string | null;
  sessions: SessionSummary[];
  activeSession?: SessionSummary;
  isLoading: boolean;
  isReady: boolean;
  error: Error | null;
};

function useForecastSessionId(): string | null {
  const location = useLocation();

  return new URLSearchParams(location.searchStr).get('sessionId');
}

export function useForecastSession(): UseForecastSessionResult {
  const location = useLocation();
  const sessionId = useForecastSessionId();
  const sessionScopedPath: '/forecast' | '/forecast/$sku' | '/configuration' =
    location.pathname.startsWith('/configuration')
      ? '/configuration'
      : location.pathname.startsWith('/forecast/')
        ? '/forecast/$sku'
        : '/forecast';
  const navigate = useNavigate({ from: sessionScopedPath });
  const shouldManageSessionId = SESSION_SCOPED_PATHS.some((path) =>
    location.pathname.startsWith(path),
  );
  const { data, isLoading, error } = useForecastSessionsQuery();
  const sessions = data ?? EMPTY_SESSIONS;

  const activeSession = useMemo(
    () => sessions.find((session) => session.id === sessionId),
    [sessions, sessionId],
  );

  useEffect(() => {
    if (!shouldManageSessionId || isLoading || error || sessions.length === 0) {
      return;
    }

    const defaultSessionId = sessions[0]?.id;
    if (!defaultSessionId || (sessionId && activeSession)) {
      return;
    }

    void navigate({
      search: (previous) => ({
        ...previous,
        sessionId: defaultSessionId,
      }),
      replace: true,
    });
  }, [
    activeSession,
    error,
    isLoading,
    navigate,
    sessionId,
    sessions,
    shouldManageSessionId,
  ]);

  return {
    sessionId,
    sessions,
    activeSession,
    isLoading,
    isReady:
      !(isLoading || error) &&
      (sessions.length === 0 || Boolean(sessionId && activeSession)),
    error: error ?? null,
  };
}
